import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  MenuItem, 
  FormControlLabel, 
  Switch, 
  Box,
  Grid,
  Typography
} from '@mui/material';

const TRAINING_TYPE_OPTIONS = [
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'heat_illness_prevention', label: 'Heat Illness Prevention' },
  { value: 'ppe', label: 'PPE' },
  { value: 'material_handling_lifting', label: 'Material Handling & Lifting Safety' },
  { value: 'machine_equipment_safety', label: 'Machine & Equipment Safety' },
  { value: 'forklifts_powered_equipment', label: 'Forklifts & Powered Equipment' },
  { value: 'warehouse_facility_safety', label: 'Warehouse & Facility Safety' },
  { value: 'general_workplace_hazards', label: 'General Workplace Hazards' },
  { value: 'emergency_preparedness', label: 'Emergency Preparedness & Response' },
  { value: 'lockout_tagout', label: 'Lockout/Tagout (LOTO) & Energy Control' },
  { value: 'calosha_compliance', label: 'Cal/OSHA Compliance & Programs' },
  { value: 'hazard_communication', label: 'Hazard Communication' },
  { value: 'ergonomics_office_safety', label: 'Ergonomics & Office Safety' },
  { value: 'workplace_conduct', label: 'Workplace Conduct & Communication' },
  { value: 'risk_hazard_assessment', label: 'Risk & Hazard Assessment' },
  { value: 'electrical_safety', label: 'Electrical Safety' },
];

export function formatTrainingTypeLabel(value?: string | null): string {
  if (!value) return 'N/A';
  const match = TRAINING_TYPE_OPTIONS.find((option) => option.value === value);
  if (match) return match.label;
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

interface TrainingFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: any;
}

const TrainingForm: React.FC<TrainingFormProps> = ({ open, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    training_type: 'general_workplace_hazards',
    is_required: true,
    is_recurring: false,
    recurrence_months: 12,
    passing_score: 80,
    has_quiz: true,
    status: 'active',
    is_active: true,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || '',
        description: initialData.description || '',
        training_type: initialData.training_type || 'general_workplace_hazards',
        is_required: initialData.is_required !== undefined ? initialData.is_required : true,
        is_recurring: initialData.is_recurring || false,
        recurrence_months: initialData.recurrence_months || 12,
        passing_score: initialData.passing_score || 80,
        has_quiz: initialData.has_quiz !== undefined ? initialData.has_quiz : true,
        status: initialData.status || 'active',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });
    } else {
      setForm({
        title: '',
        description: '',
        training_type: 'general_workplace_hazards',
        is_required: true,
        is_recurring: false,
        recurrence_months: 12,
        passing_score: 80,
        has_quiz: true,
        status: 'active',
        is_active: true,
      });
    }
  }, [initialData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value === '' ? 0 : parseInt(value, 10),
    }));
  };

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Edit Training' : 'Add Training'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Title"
              name="title"
              value={form.title}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Training Type"
              name="training_type"
              value={form.training_type}
              onChange={handleChange}
              select
              fullWidth
            >
              {TRAINING_TYPE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Status"
              name="status"
              value={form.status}
              onChange={handleChange}
              select
              fullWidth
            >
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Training Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Training Settings</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_required"
                  checked={form.is_required}
                  onChange={handleChange}
                />
              }
              label="Required Training"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
              }
              label="Active"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_recurring"
                  checked={form.is_recurring}
                  onChange={handleChange}
                />
              }
              label="Recurring Training"
            />
          </Grid>
          
          {form.is_recurring && (
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Recurrence (months)"
                name="recurrence_months"
                type="number"
                value={form.recurrence_months}
                onChange={handleNumberChange}
                fullWidth
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
          )}

          {/* Test Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Test Settings</Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="has_quiz"
                  checked={form.has_quiz}
                  onChange={handleChange}
                />
              }
              label="Include Test"
            />
          </Grid>
          
          {form.has_quiz && (
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Passing Score (%)"
                name="passing_score"
                type="number"
                value={form.passing_score}
                onChange={handleNumberChange}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {initialData ? 'Save Changes' : 'Create Training'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingForm; 